import { Tooltip } from "@material-ui/core";
import clsx from "clsx";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Select, { OptionTypeBase, ValueType } from "react-select";
import CreatableSelect from "react-select/creatable";

import {
  CustomDestinationOption,
  DestinationSelect,
} from "../../../components/DestinationSelect";
import {
  AddIcon,
  GoalIcon,
  MandatoryIcon,
} from "../../../components/icons/themes/windows10";
import {
  allDestinationsData,
  DestinationDefinition,
  destinationKeyToSlugMap,
  SaaSDestination,
} from "../../../destinations/destinations";
import {
  DestinationKey,
  DestinationName,
  DestinationSlug,
  DestinationStandardMapping,
  DestinationStandardMappingItemNamed,
  DestinationStandardMappingItemWildcard,
} from "../../../destinations/types";
import { makeRequest } from "../../../util";
import { TransformDefinitionSet } from "../../transformationDefinition";
import { TransformationIntention } from "../../types";
import {
  LoadedEventDefintion,
  LoadedDestinationInfo,
} from "../../../types/global";
import { stringDistance } from '../../utils';
import {
  destinationIsValidForEvent,
  eventDefType,
  libraryForEventType,
} from "../../../eventTypes";

const selectStyles = {
  option: (base: object, { isSelected }: { isSelected: boolean }) => ({
    ...base,
    color: "#444",
    backgroundColor: isSelected ? "#93E5D3" : "white",
  }),
};

const largeSelectStyles = {
  ...selectStyles,
  container: (base: object) => ({
    ...base,
    display: "inline-block",
    height: 60,
    marginBottom: 10,
  }),
  menu: (base: object) => ({
    ...base,
    top: 60,
  }),
  valueContainer: (base: object) => ({
    ...base,
    minHeight: 60,
  }),
};

const sortedEventMappingForEvent = (
  fpEventName: string,
  destinationMappingItems: DestinationStandardMappingItemNamed[]
) => {
  return _.orderBy(
    destinationMappingItems,
    (item, index) => {
      const distance = stringDistance(fpEventName, item.eventName);
      if (distance < 10) {
        return distance;
      } else {
        return 10 + index;
      }
    },
    "asc"
  );
};
const WildcardCustomOption: React.FC<{
  wildcardConfig: DestinationStandardMappingItemWildcard | null;
  inputValue: string;
}> = ({ wildcardConfig, inputValue }) => (
  <>
    <div className="text-md text-gray-800">
      <Tooltip title="New standard mapping">
        <AddIcon className="mr-2" style={{ position: "relative", top: -2 }} />
      </Tooltip>
      {inputValue}
    </div>
    {wildcardConfig && (
      <div className="text-sm text-gray-600">{wildcardConfig.description}</div>
    )}
  </>
);

const getEnabledDestinationsForEventDef = (
  enabledDests: Record<string, boolean>,
  availableDestsIDToName: Record<string, DestinationKey>
): Record<DestinationKey, boolean> => {
  const result: Record<DestinationName, boolean> = {};

  for (let enabledDestID in enabledDests) {
    let isEnabled = enabledDests[enabledDestID];
    let destName = availableDestsIDToName[enabledDestID];
    result[destName] = isEnabled;
  }

  return result;
};

export const ALL_DESTINATIONS_VALUE = "ALL_DESTINATIONS_VALUE";
export const ALL_EVENTS_VALUE = "ALL_EVENTS_VALUE";

const allDestinationOption: CustomDestinationOption = {
  value: ALL_DESTINATIONS_VALUE,
  icon: <></>,
  label: <em>All Destinations</em>,
};
const allEventsOption = {
  value: ALL_EVENTS_VALUE,
  label: <em>All Events</em>,
};

const SelectStandardEventWithWildcard: React.FC<{
  selectedDestinationDefinition: DestinationDefinition;
  selectedDestinationMappingEventOptions: Array<{
    value: string;
    label: React.ReactNode;
  }>;
  setMappingEvent: (option: ValueType<OptionTypeBase>) => void;
  selectedDestinationMappingEventSelectedOption:
    | {
        value: string;
        label: React.ReactNode;
      }
    | null
    | undefined;
  selectedDestinationStandardMappingConfig: DestinationStandardMapping;
}> = ({
  selectedDestinationDefinition,
  selectedDestinationMappingEventOptions,
  setMappingEvent,
  selectedDestinationMappingEventSelectedOption,
  selectedDestinationStandardMappingConfig,
}) => {
  const wildcard: DestinationStandardMappingItemWildcard = selectedDestinationStandardMappingConfig.wildcard as DestinationStandardMappingItemWildcard;
  const mappingName =
    selectedDestinationDefinition instanceof SaaSDestination
      ? selectedDestinationDefinition.eventMappingEventName
      : "";
  const noOptionsMsg =
    `Start typing to add your own ${
      wildcard.description || mappingName
    }`.trim() + "...";

  const formatNewLabel = (inputValue: string) => (
    <>
      <div className="text-md text-gray-800">
        <Tooltip title="Add a new standard mapping">
          <AddIcon className="mr-2" style={{ position: "relative", top: -2 }} />
        </Tooltip>
        Add <code>{inputValue}</code> event
      </div>
      {wildcard.description && (
        <div className="text-sm text-gray-600">
          Add any additional event that you’ve defined for the purpose of
          audience targeting. Unique events aren’t available for conversion
          reporting. Keep in mind that whitespace will be trimmed from raw event
          names passed through the tag.
        </div>
      )}
    </>
  );

  const formatNewOption = (inputValue: string) => ({
    value: inputValue,
    label: (
      <WildcardCustomOption
        wildcardConfig={selectedDestinationStandardMappingConfig.wildcard}
        inputValue={inputValue}
      />
    ),
  });

  return (
    <CreatableSelect
      className="mb-4 mt-4 w-full h-6"
      placeholder={`${selectedDestinationDefinition.name} Standard Mappings...`}
      options={selectedDestinationMappingEventOptions}
      onChange={setMappingEvent}
      value={selectedDestinationMappingEventSelectedOption}
      styles={largeSelectStyles}
      isValidNewOption={(inputValue) => !!inputValue}
      createOptionPosition="last"
      noOptionsMessage={() => noOptionsMsg}
      formatCreateLabel={formatNewLabel}
      getNewOptionData={formatNewOption}
    />
  );
};

export const NewTransformationPageStep2: React.FC<{
  intention: TransformationIntention;
  eventDefinitions: LoadedEventDefintion[];
  selectDestination: (
    destinationID: ValueType<CustomDestinationOption>
  ) => void;
  selectedDestination?: DestinationKey | typeof ALL_DESTINATIONS_VALUE | null;
  selectedDestinationDefinition: DestinationDefinition | null;
  selectedDestinationConfig: LoadedDestinationInfo | null;
  selectEventDef: (eventDefID?: ValueType<OptionTypeBase>) => void;
  selectedDestinationStandardEvent?: DestinationStandardMappingItemNamed | null;
  setSelectedDestinationStandardEvent: (
    standardEvent: DestinationStandardMappingItemNamed | null
  ) => void;
  selectedEventDef?: string | typeof ALL_EVENTS_VALUE | null;
  selectedEventDefConfig?: LoadedEventDefintion;
  setPropertyMapping: (properties: Record<string, string>) => void;
  propertyMapping: Record<string, string>;
  refreshEventDefinitions: () => void;
  duplicateTransformationSet: TransformDefinitionSet | null;
  availableDestsIDToKey: Record<string, DestinationKey>;
}> = ({
  eventDefinitions,
  intention,
  selectDestination,
  selectEventDef,
  selectedDestination,
  selectedEventDef,
  selectedEventDefConfig,
  setPropertyMapping,
  selectedDestinationStandardEvent,
  setSelectedDestinationStandardEvent,
  selectedDestinationDefinition,
  refreshEventDefinitions,
  duplicateTransformationSet,
  availableDestsIDToKey,
  selectedDestinationConfig,
}) => {
  const history = useHistory();

  const enabledDestinationsForEventDef: Partial<Record<
    DestinationKey | typeof ALL_DESTINATIONS_VALUE,
    boolean
  >> = selectedEventDefConfig
    ? getEnabledDestinationsForEventDef(
        selectedEventDefConfig.enabled_dests,
        availableDestsIDToKey
      )
    : {};
  const [enabling, setEnabling] = useState(false);
  const [
    selectedStandardMappingEventName,
    selectStandardMappingEventName,
  ] = useState<string | null>(null);
  const [
    selectedDestinationStandardMappingConfig,
    setSelectedDestinationStandardMappingConfig,
  ] = useState<DestinationStandardMapping | null>(null);
  const selectedEventDefName = selectedEventDefConfig
    ? selectedEventDefConfig.name
    : null;
  let selectedDestinationMappingEventOptions: Array<{
      value: string;
      label: React.ReactNode;
    }> = [],
    selectedDestinationMappingEventSelectedOption = null;

  let hasSelectedAllDestinations =
    selectedDestination === ALL_DESTINATIONS_VALUE;
  let hasSelectedAllEvents = selectedEventDef === ALL_EVENTS_VALUE;

  if (selectedDestination && selectedDestinationDefinition) {
    if (
      selectedEventDefName &&
      selectedDestinationDefinition.type == "advertising" &&
      selectedDestinationDefinition.hasEventMapping &&
      selectedDestinationStandardMappingConfig
    ) {
      selectedDestinationMappingEventOptions = _.map(
        sortedEventMappingForEvent(
          selectedEventDefName,
          selectedDestinationStandardMappingConfig.items
        ),
        (event, index) => ({
          value: event.eventName,
          label: (
            <>
              <div className="text-md text-gray-800">
                {event.eventName}{" "}
                {index === 0 &&
                  stringDistance(selectedEventDefName, event.eventName) < 8 && (
                    <Tooltip title="This looks like a good match">
                      <GoalIcon
                        style={{ position: "relative", top: -2 }}
                        className="text-turquoise-800"
                      />
                    </Tooltip>
                  )}
              </div>
              <div className="text-sm text-gray-600">{event.description}</div>
            </>
          ),
        })
      );
      selectedDestinationMappingEventSelectedOption = selectedDestinationStandardEvent
        ? _.find(
            selectedDestinationMappingEventOptions,
            (opt) => opt.value === selectedDestinationStandardEvent.eventName
          )
        : null;
      if (
        selectedDestinationStandardEvent &&
        !selectedDestinationMappingEventSelectedOption &&
        selectedDestinationStandardMappingConfig.wildcard
      ) {
        selectedDestinationMappingEventSelectedOption = {
          value: selectedDestinationStandardEvent.eventName,
          label: (
            <WildcardCustomOption
              inputValue={selectedDestinationStandardEvent.eventName}
              wildcardConfig={selectedDestinationStandardMappingConfig.wildcard}
            />
          ),
        };
      }
    }
  }

  useEffect(() => {
    const update = async () => {
      if (
        selectedDestinationDefinition &&
        selectedDestinationDefinition.type == "advertising" &&
        selectedDestinationDefinition.getStandardMapping
      ) {
        const mapping = await selectedDestinationDefinition.getStandardMapping();
        setSelectedDestinationStandardMappingConfig(mapping);
      } else {
        setSelectedDestinationStandardMappingConfig(null);
      }
    };
    update();
  }, [selectedDestinationDefinition]);

  useEffect(() => {
    const mappingDict: Record<string, string> = {};
    if (selectedDestinationStandardEvent) {
      _.forEach(selectedDestinationStandardEvent.propertiesArr, (prop) => {
        mappingDict[prop.key] = prop.key;
      });
    }
    setPropertyMapping(mappingDict);
  }, [selectedDestinationStandardEvent, setPropertyMapping]);

  useEffect(() => {
    if (selectedDestinationStandardMappingConfig) {
      let foundItem = _.find(
        selectedDestinationStandardMappingConfig.items,
        (opt) => opt.eventName === selectedStandardMappingEventName
      );
      if (
        !foundItem &&
        selectedDestinationStandardMappingConfig.wildcard &&
        selectedStandardMappingEventName
      ) {
        foundItem = {
          propertiesArr:
            selectedDestinationStandardMappingConfig.wildcard.propertiesArr,
          eventName: selectedStandardMappingEventName,
        };
      }
      setSelectedDestinationStandardEvent(foundItem || null);
    } else {
      setSelectedDestinationStandardEvent(null);
    }
  }, [
    selectedDestinationStandardMappingConfig,
    selectedStandardMappingEventName,
    setSelectedDestinationStandardEvent,
  ]);

  const setMappingEvent = (option: ValueType<OptionTypeBase>) => {
    if (option) {
      // @ts-ignore
      selectStandardMappingEventName(option.value);
    } else {
      selectStandardMappingEventName(null);
    }
  };

  const makeOption = (
    destinationKey: DestinationSlug
  ): CustomDestinationOption | null => {
    if (!destinationKey) {
      return null;
    }
    const destinationSlug = destinationKeyToSlugMap[destinationKey];
    const dest = allDestinationsData[destinationSlug];
    const Icon = dest.logoSquare;

    return {
      value: dest.key,
      icon: <Icon />,
      label: dest.name,
    };
  };

  const isAdvertisingDestinations = (
    destinationKey: DestinationKey
  ): boolean => {
    const destinationSlug = destinationKeyToSlugMap[destinationKey];
    const dest = allDestinationsData[destinationSlug];
    return dest.type === "advertising" && dest.hasEventMapping;
  };

  const destinationIsDisabledForEventDef =
    selectedDestination &&
    !enabledDestinationsForEventDef[selectedDestination];

  let filteredDestinations =
    intention === "standardEvent"
      ? _.filter(availableDestsIDToKey, isAdvertisingDestinations)
      : availableDestsIDToKey;

  // @ts-ignore
  let destinationOptions: CustomDestinationOption[] = _.chain(
    filteredDestinations
  )
    .map(makeOption)
    .filter((o) => !!o)
    .orderBy("name")
    .value();

  let eventDefOptions: OptionTypeBase[] = _.map(eventDefinitions, (def) => ({
    value: def.id,
    label: def.name,
  }));

  if (intention === "fixBadData") {
    destinationOptions = _.concat([allDestinationOption], destinationOptions);
    eventDefOptions = _.concat([allEventsOption], eventDefOptions);
  }

  const selectedDestinationOption = _.find(
    destinationOptions,
    (opt) => opt.value === selectedDestination
  );
  const selectedEventDefOption = _.find(
    eventDefOptions,
    (opt) => opt.value === selectedEventDef
  );

  const enableDestination = () => {
    if (selectedDestination && selectedEventDef) {
      setEnabling(true);
      makeRequest<any>("toggle_destination_by_name", {
        dest_name: selectedDestination,
        event_def_id: selectedEventDef,
        state: true,
      }).then(() => {
        refreshEventDefinitions();
        setEnabling(false);
      });
    }
  };

  return (
    <div
      className="flex justify-center flex-col items-center mt-8"
      key={`step-2-${intention}`}
    >
      <div className="w-1/3 flex flex-col justify-between">
        <Select
          className="mb-4 w-full"
          placeholder="Choose an event to transform..."
          options={eventDefOptions}
          onChange={selectEventDef}
          value={selectedEventDefOption}
          styles={selectStyles}
        />
        <DestinationSelect
          className="w-full mb-4"
          onChange={selectDestination}
          options={destinationOptions}
          value={selectedDestinationOption}
          styles={selectStyles}
        />
        {!hasSelectedAllDestinations &&
          !hasSelectedAllEvents &&
          selectedEventDefConfig &&
          selectedDestinationConfig &&
          destinationIsDisabledForEventDef &&
          selectedDestinationConfig &&
          (destinationIsValidForEvent(
            selectedDestinationConfig,
            libraryForEventType(eventDefType(selectedEventDefConfig)) !== "web"
          ) ? (
            <div className="text-sm text-red-600 mt-1">
              <MandatoryIcon style={{ position: "relative", top: -2 }} /> This
              destination isn't enabled for this event.{" "}
              <span
                className={clsx("underline", {
                  "opacity-50": enabling,
                  "cursor-pointer": !enabling,
                })}
                onClick={enableDestination}
              >
                Enable it?
              </span>
            </div>
          ) : (
            <div className="text-sm text-red-600 mt-1">
              <MandatoryIcon style={{ position: "relative", top: -2 }} /> This
              event cannot be sent to this destination.
            </div>
          ))}

        {intention === "fixBadData" &&
          hasSelectedAllDestinations &&
          hasSelectedAllEvents && (
            <div className="text-sm text-red-600 mt-1">
              <MandatoryIcon /> You must choose an event, a destination or both.
            </div>
          )}
        {duplicateTransformationSet && (
          <div className="text-sm text-red-600 mt-1">
            <MandatoryIcon /> You already have a transformation for this event +
            destination combination.{" "}
            <span
              className="underline cursor-pointer"
              onClick={() =>
                history.push(
                  `/transformations/edit/${duplicateTransformationSet.id}`
                )
              }
            >
              View it here.
            </span>
          </div>
        )}
        {!duplicateTransformationSet && (
          <>
            {intention === "standardEvent" &&
              selectedDestinationDefinition &&
              selectedDestinationDefinition.type === "advertising" && (
                <div className="text-lg text-gray-700 mt-1">
                  {selectedDestinationDefinition.eventMappingBodyHelp}
                </div>
              )}

            {/* Event Mapping Choice */}
            {intention === "standardEvent" &&
              selectedEventDef &&
              selectedDestinationDefinition &&
              selectedDestinationMappingEventOptions &&
              !duplicateTransformationSet &&
              (!!selectedDestinationStandardMappingConfig &&
              selectedDestinationStandardMappingConfig.wildcard ? (
                <SelectStandardEventWithWildcard
                  selectedDestinationDefinition={selectedDestinationDefinition}
                  selectedDestinationMappingEventOptions={
                    selectedDestinationMappingEventOptions
                  }
                  selectedDestinationMappingEventSelectedOption={
                    selectedDestinationMappingEventSelectedOption
                  }
                  selectedDestinationStandardMappingConfig={
                    selectedDestinationStandardMappingConfig
                  }
                  setMappingEvent={setMappingEvent}
                />
              ) : (
                <Select
                  className="mb-4 mt-4 w-full h-6"
                  // @ts-ignore
                  placeholder={
                    selectedDestinationDefinition instanceof SaaSDestination &&
                    selectedDestinationDefinition.eventMappingEventName
                      ? selectedDestinationDefinition.eventMappingEventName
                      : `${selectedDestinationDefinition.name} Standard Mappings...`
                  }
                  options={selectedDestinationMappingEventOptions}
                  onChange={setMappingEvent}
                  value={selectedDestinationMappingEventSelectedOption}
                  styles={largeSelectStyles}
                  isSearchable={false}
                />
              ))}
          </>
        )}
      </div>
    </div>
  );
};
